/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {InputAdornment} from '@material-ui/core';
import {VisibilityOff} from '@material-ui/icons';
import VisibilityIcon from '@material-ui/icons/Visibility';
import useSecureField from '../../hooks/useSecureField';
import {AutoCompleteTypes, InputTypes} from '../../types/editors';
import {IconButton} from '../buttons/IconButton';
import {Props} from '../textfield/TextField';
import {FormTextField} from './FormTextField';

export interface FormSecureTextField extends Props {
  fallbackValue?: string;
  hideCharacters?: boolean;
  showToggleButton?: boolean;
}

/**
 * @FormSecureTextField
 * Component that wraps the TextField with styles that are specific to the
 * a Form (including ability to show / hide entered text).
 *
 * @param {object} props pass through props that get sent to TextField
 *
 * ```typescript
 *
 * import {FormSecureTextField} from '@ion/dls/src/components/forms/';
 *
 * <FormSecureTextField {...props} />
 *
 * ```
 */
export const FormSecureTextField = ({
  value,
  fallbackValue,
  showToggleButton = true,
  hideCharacters = true,
  autoComplete = AutoCompleteTypes.NEW_PASSWORD,
  ...rest
}: FormSecureTextField) => {
  const {
    value: secureValue,
    inputType,
    toggleVisibility,
    onFocus,
    onBlur
  } = useSecureField({
    formValue : value as string,
    fallbackValue,
    hideCharacters
  });

  return (
    <FormTextField type={inputType}
      autoComplete={autoComplete}
      InputProps={{
        endAdornment : showToggleButton ? (
          <InputAdornment position='end'>
            <IconButton onClick={toggleVisibility}
              icon={inputType === InputTypes.PASSWORD ? <VisibilityOff /> : <VisibilityIcon />}
              disabled={secureValue === '' || secureValue === 'placeholder'}
              size='small' />
          </InputAdornment>
        ) : undefined
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      value={secureValue}
      {...rest} />
  );
};

export default FormSecureTextField;
